import React, { useState, useEffect } from "react";
import BlockEdit from "./Blocks/BlockEdit";
import NewBlockButton from "./Blocks/NewBlockButton";
import DraggableElement from "../../../Shared/DraggableElement";
import _ from "lodash";

const TemplateEdit = ({ template: initialTemplate, onChange: onChangeProp, groupId }) => {
    const [template, setTemplate] = useState({
        id: 0,
        name: "",
        blocks: [],
    });
    const [expended, setExpended] = useState([]);
    const [hoveredId, setHoveredId] = useState(undefined);
    const [dragging, setDragging] = useState(false);

    useEffect(() => {
        if (initialTemplate) {
            setTemplate({
                id: initialTemplate.id,
                name: initialTemplate.name,
                blocks: initialTemplate.blocks,
            });
        }
    }, [initialTemplate]);

    const handleChange = (block) => {
        const updatedBlocks = template.blocks.map((b) => (b.id === block.id ? block : b));
        const updatedTemplate = { ...template, blocks: updatedBlocks };
        setTemplate(updatedTemplate);
        onChangeProp(updatedTemplate);
    };

    const handleCreate = (block, index = null) => {
        let newBlocks;
        if (index !== null) {
            newBlocks = [...template.blocks];
            newBlocks.splice(index + 1, 0, block);
        } else {
            if (block.type === "HEADER") {
                newBlocks = [block, ...template.blocks];
            } else if (block.type === "FOOTER") {
                newBlocks = [...template.blocks, block];
            } else {
                const firstFooterIndex = template.blocks.findIndex(
                    (b) => b.type === "FOOTER"
                );
                if (firstFooterIndex !== -1) {
                    newBlocks = [...template.blocks];
                    newBlocks.splice(firstFooterIndex, 0, block);
                } else {
                    newBlocks = [...template.blocks, block];
                }
            }
        }
        const updatedTemplate = { ...template, blocks: newBlocks };
        setTemplate(updatedTemplate);
        onChangeProp(updatedTemplate);
    };

    const handleDelete = (block) => {
        const updatedBlocks = template.blocks.filter((b) => b !== block);
        const updatedTemplate = { ...template, blocks: updatedBlocks };
        setTemplate(updatedTemplate);
        onChangeProp(updatedTemplate);
    };

    const moveBlockInGroup = (index, groupId) => {
        const newBlocks = [...template.blocks];
        const group = newBlocks.find((b) => b.id === groupId);
        if (group?.type !== "GROUP") return;

        const [block] = newBlocks.splice(index, 1);
        group.data.blocks.push(block);

        setExpended([...expended, groupId]);
        const updatedTemplate = { ...template, blocks: newBlocks };
        setTemplate(updatedTemplate);
        onChangeProp(updatedTemplate);
    };

    const move = (oldIndex, newIndex) => {
        setDragging(false);
        if (hoveredId !== undefined) {
            moveBlockInGroup(oldIndex, hoveredId);
            return;
        }
        if (oldIndex === newIndex) return;

        const moveBlock = (arr, from, to) => {
            arr.splice(to, 0, arr.splice(from, 1)[0]);
            return arr;
        };

        const updatedBlocks = moveBlock([...template.blocks], oldIndex, newIndex);
        const updatedTemplate = { ...template, blocks: updatedBlocks };
        setTemplate(updatedTemplate);
        onChangeProp(updatedTemplate);
    };

    const toggleExpendBlock = (block) => {
        setExpended(
            expended.includes(block.id)
                ? expended.filter((id) => id !== block.id)
                : [...expended, block.id]
        );
    };

    const renderBlocks = () => {
        if (!template.blocks) return null;

        return (
            <DraggableElement
                list={template.blocks}
                onDragEnd={(oldIndex, newIndex) => move(oldIndex, newIndex)}
                onDragStart={() => setDragging(true)}
                context={!groupId}
            >
                {template.blocks.map((block, index) => (
                    <div key={index}>
                        <div
                            style={{ padding: 0, borderRadius: "20px" }}
                            id={`block_${block.id}`}
                        >
                            <BlockEdit
                                block={block}
                                onChange={handleChange}
                                onDelete={handleDelete}
                                expended={expended.includes(block.id)}
                                onExpended={toggleExpendBlock}
                                isFirst={index === 0}
                                isLast={index === template.blocks.length - 1}
                                hovered={hoveredId === block.id && dragging}
                                isHovered={(hovered) =>
                                    setHoveredId(hovered ? block.id : undefined)
                                }
                                dragging={dragging}
                                groupId={groupId}
                            />
                        </div>
                        <div
                            style={{ width: "100%" }}
                            className="center"
                        >
                            <NewBlockButton
                                onValidation={(newBlock) =>
                                    handleCreate(newBlock, index)
                                }
                                betweenBlocks={true}
                                id={`new_block_${
                                    groupId ? `group_${groupId}_` : ""
                                }${index}`}
                            />
                            <div
                                className={
                                    index === template.blocks.length - 1
                                        ? ""
                                        : "hide"
                                }
                                style={{ paddingBottom: "40px" }}
                            />
                        </div>
                    </div>
                ))}
            </DraggableElement>
        );
    };

    return (
        <div
            className="transparent"
            style={{ height: "100%", padding: "2vh 1vw 2vh 1vw" }}
        >
            <div className="center">
                <NewBlockButton
                    disabled={!initialTemplate || _.isEmpty(initialTemplate)}
                    onValidation={handleCreate}
                    id={`general_${template.id}`}
                    inGroup={!!groupId}
                />
            </div>
            <div style={{ paddingTop: "2vh" }}>{renderBlocks()}</div>
        </div>
    );
};

TemplateEdit.defaultProps = {
    name: "Template",
    groupId: null,
};

export default TemplateEdit;
